/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { ApiQuery } from '@app/core/http/api-query';
import { FormGroup } from '@angular/forms';
import { City } from '@app/settings/cities/cities.model';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { CitiesService } from '@app/settings/cities/cities.service';
import { Logger } from '@app/core';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { CitiesFormService } from '@app/settings/cities/cities-form.service';

const log = new Logger('CitiesSelectComponent');

@Component({
  selector: 'app-cities-select',
  templateUrl: './cities-select.component.html',
  styleUrls: ['./cities-select.component.scss'],
})
export class CitiesSelectComponent implements OnInit {
  public get selectedCity(): City {
    return this._selectedCity;
  }

  data: Observable<City[]>;
  apiQuery: ApiQuery = new ApiQuery();
  loader: boolean;
  @Input() parentFormGroup: FormGroup;
  @Input() fillParentFormCoordinates = false;
  @Input() hideLabel = false;
  @Input() onlyActive = true;
  @Input() isInModal = true;
  @Output() select = new EventEmitter();
  input$ = new Subject<string>();
  lastResponse: any = null;
  private _selectedCity: City = null;

  constructor(private cityService: CitiesService, private cityFormService: CitiesFormService) {}

  ngOnInit() {
    const preSelectedCity = this.parentFormGroup.get('city');
    const preSelectedCityId = this.parentFormGroup.get('city_id');

    /**
     * Попълва избрания хотел на едит.
     * Ако има FormControl 'city' в parentFormGroup.
     * Ако няма ще се опита да го get-не по ID
     */
    if (preSelectedCity && preSelectedCity.value && preSelectedCity.value.name) {
      /**
       * По някой път city-то идва готово от първия път и valueChange-a не се пали
       * Примерно в user формата
       */
      preSelectedCity.value['fullName'] = preSelectedCity.value.name + ' (' + preSelectedCity.value.country.name + ')';
      this.data = of([preSelectedCity.value]);
    } else if (preSelectedCity) {
      /*
       * Следи за промени в city FormControl-a и когато намери валиден City спира да следи
       */
      const preselectedCitySubscriber = preSelectedCity.valueChanges.subscribe((city: City) => {
        if (city) {
          city = new City().deserialize(city); // за да могат да се викат get-ерите
          this.data = of([city]);
        }
        preselectedCitySubscriber.unsubscribe();
      });
    } else if (preSelectedCityId) {
      /*
       * Ако не е намерен city FormControl-a търси city_id FormControl и се опитва да get-не city-то по ID
       */
      const preselectedCityIdSubscriber = preSelectedCityId.valueChanges.subscribe((cityId: any) => {
        if (cityId) {
          this.loadCityById(cityId);
          preselectedCityIdSubscriber.unsubscribe();
        }
      });
    }

    this.citiesSearch();
  }

  // loadCities(keyword?: string) {
  //   this.loader = true;
  //   const query = this.apiQuery.addInclude('country').addFilter('keywords', keyword);
  //   this.cityService.index(query).subscribe((response: PaginatedResponse) => {
  //     this.data = of(
  //       response.data.map(i => {
  //         i.fullName = i.name + ' (' + i.country.name + ')';
  //         return i;
  //       })
  //     );
  //     this.loader = false;
  //   });
  // }

  loadCityById(id: number) {
    this.loader = true;
    const query = new ApiQuery().addInclude('country');
    this.cityService.show(id, query).subscribe((city: City) => {
      this.data = of([city]);
      this.loader = false;
    });
  }

  citiesSearch() {
    const query = this.apiQuery.addInclude('country');

    if (this.onlyActive) {
      query.addFilter('active', true);
    }

    this.input$
      .pipe(
        tap(() => (this.loader = true)),
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((term: any) => {
          // За да ме прави излишен request след като се избере нещо
          if (!term) {
            return of(this.lastResponse);
          }
          if (this.parentFormGroup.get('country_id') && this.parentFormGroup.get('country_id').value) {
            query.addFilter('country_id', this.parentFormGroup.get('country_id').value);
          }

          return this.cityService.index(query.addFilters({ keywords: term })).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.loader = false))
          );
        })
      )
      .subscribe((cities: PaginatedResponse) => {
        this.data = of(cities.data);
        this.lastResponse = cities;
        this.loader = false;
      });
  }

  onChange(city: any) {
    this.fillCoordinates(city);
    this._selectedCity = city;
    this.select.emit(city);
  }

  add(searchTerm: string) {
    this.cityFormService.open(new City().deserialize({ name: searchTerm })).then(
      (response) => {
        this.loadCityById(response.id);
        this.parentFormGroup.get('city_id').setValue(response.id);
      },
      (err) => {
        log.debug(err);
      }
    );
  }

  edit() {
    if (this.parentFormGroup.get('city_id').value) {
      this.cityFormService.open(new City().deserialize({ id: this.parentFormGroup.get('city_id').value })).then(
        (response) => {
          this.loadCityById(response.id);
          this.parentFormGroup.get('city_id').setValue(response.id);
          if (this.parentFormGroup.get('city')) {
            this.parentFormGroup.get('city').patchValue(response);
          }
        },
        (err) => {
          log.error(err);
        }
      );
    }
  }

  /**
   * Проверява дали компонента се вика с [fillParentFormCoordinates]="true"
   * Ако има - при промяна на избраната държава ще сетва lat и long АКО ИМА ТАКИВА FormControl-и
   * @param city fillCoordinates
   */
  private fillCoordinates(city: any) {
    if (this.fillParentFormCoordinates) {
      const parentFormControlLat = this.parentFormGroup.get('lat');
      const parentFormControlLong = this.parentFormGroup.get('long');

      /*
       * Ако parent формта няма такива FormControl-и прикючваме до тук
       */
      if (!parentFormControlLat || !parentFormControlLong) {
        return log.error('Cant`t find parent form lat long from controls.');
      }

      if (!city) {
        return false;
      }

      /*
       * Пълни FormControl-ите с тези от избрания град града
       */
      parentFormControlLat.setValue(Number(city.lat));
      parentFormControlLong.setValue(Number(city.long));
    }
  }
}
